/* eslint-disable complexity, new-cap, react/no-multi-comp */
import React from 'react';
import { string } from '@mooveguru/js-utilities';
import { translate } from '../../Internationalization';
import {
	createMarketplaceVendorRows,
	getCategoryLabel,
	getCategorySections,
} from './PropertyHomePros.helpers';
import flags from '../../../shared/Flags';
import images from '../../../config/local/images';
import Loading from '../../../shared/Loading';
import NoContent from '../../../shared/NoContent';
import OfferCard from '../../../Marketplace/OfferCard';
import ExternalAnchor from '../../../shared/ExternalAnchor';

const ROW_CLASS =
	'flex items-center justify-between gap-4 py-2 border-b border-grey-200';

/**
 * @param {{ label: string, onAdd?: (() => void), onRemove?: (() => void), addDisabled?: boolean, removeDisabled?: boolean, showRemove?: boolean }} props
 * @returns {JSX.Element}
 */
export function CategoryRow(props) {
	return (
		<section className={ROW_CLASS}>
			<span>{props.label}</span>
			<section className="flex items-center gap-2 flex-shrink-0">
				<button
					className="button button-sm button-outline"
					disabled={props.addDisabled}
					onClick={props.onAdd}
					type="button"
				>
					{'Add'}
				</button>
				{props.showRemove === false ? null : (
					<button
						className="button button-sm button-outline"
						disabled={props.removeDisabled}
						onClick={props.onRemove}
						type="button"
					>
						{'Remove'}
					</button>
				)}
			</section>
		</section>
	);
}

/**
 * @param {{ settings: any, theme: any, vendor: any }} props
 * @returns {boolean}
 */
function isRecommendedVendor(props) {
	return (
		props.vendor.related?.type === 'agent' &&
		props.vendor.related?.id === props.theme.agentId &&
		props.settings.isEnabled(flags.vendor.add, 'homeowner_tier')
	);
}

/**
 * @param {{ settings: any, theme: any, title: string, vendors: any[] }} props
 * @returns {JSX.Element | null}
 */
function OfferSection(props) {
	if (props.vendors.length < 1) {
		return null;
	}

	return (
		<section className="mt-6">
			<h3 className="hl-ms-6">{props.title}</h3>
			<section className="mt-6 grid gap-6 sm:grid-cols-2">
				{props.vendors.map((vendor) => {
					const recommended = isRecommendedVendor({
						settings: props.settings,
						theme: props.theme,
						vendor,
					});

					return (
						<OfferCard
							headline={vendor.headline}
							isRecommended={recommended}
							key={vendor.id}
							logoUrl={vendor.logoUrl}
							name={vendor.name}
							occupation={props.theme.agentOccupation}
							phoneNumber={
								recommended
									? props.theme?.agentPhoneNumber ??
									  vendor.phoneNumber ??
									  ''
									: vendor.phoneNumber ?? ''
							}
							websiteUrl={vendor.websiteUrl}
						/>
					);
				})}
			</section>
		</section>
	);
}

/**
 * @param {any} vendor
 * @returns {JSX.Element}
 */
function MarketplaceHeadlineCard(vendor) {
	const headline = string.truncate(vendor.headline || vendor.name, 45);
	const content = (
		<div className="marketplace-tenant-card">
			<div className="marketplace-tenant-card__logo-shell">
				<img
					alt={`The ${vendor.name} logo`}
					className="marketplace-tenant-card__logo"
					src={vendor.logoUrl || images.marketplace.logoPlaceholder}
				/>
			</div>
			<p className="marketplace-tenant-card__headline">{headline}</p>
		</div>
	);

	if (!vendor.websiteUrl) {
		return (
			<div className="marketplace-tenant-card-link" key={vendor.id}>
				{content}
			</div>
		);
	}

	return (
		<ExternalAnchor
			className="marketplace-tenant-card-link"
			dataActivityClick="marketplace_click"
			dataActivityId={vendor.name}
			href={vendor.websiteUrl}
			key={vendor.id}
			renderSpan={false}
		>
			{content}
		</ExternalAnchor>
	);
}

/**
 * @param {{ title: string, vendors: any[] }} props
 * @returns {JSX.Element | null}
 */
function MarketplaceHeadlineSection(props) {
	if (props.vendors.length < 1) {
		return null;
	}

	const rows = createMarketplaceVendorRows([...props.vendors]);

	return (
		<section className="mt-6">
			<h2 className="hl-ms-6 marketplace-header__title mb-5">
				{props.title}
			</h2>
			<div className="marketplace-tenant-rows">
				{rows.map((row, index) => {
					const rowStyle = {
						'--tenant-row-columns': row.length,
						'--tenant-row-columns-tablet': Math.min(row.length, 2),
					};

					return (
						<div
							className="marketplace-tenant-row"
							key={`marketplace-row-${index + 1}`}
							// @ts-ignore -- CSS custom properties are supported at runtime.
							style={rowStyle}
						>
							{row.map((vendor) =>
								MarketplaceHeadlineCard(vendor)
							)}
						</div>
					);
				})}
			</div>
			<hr className="mt-6 border-grey-100" />
		</section>
	);
}

/**
 * @param {{ vendors: any[] }} props
 * @returns {JSX.Element | null}
 */
export function PreferredVendorSection(props) {
	if (props.vendors.length < 1) {
		return null;
	}

	return (
		<section className="mt-10">
			<h2 className="hl-ms-6 mb-6">{'My Preferred Vendors'}</h2>
			<section className="grid gap-6 sm:grid-cols-2">
				{props.vendors.map((vendor) => (
					<OfferCard
						headline={vendor.headline ?? vendor.name}
						isRecommended={false}
						key={vendor.id}
						logoUrl={vendor.logoUrl}
						name={vendor.name}
						occupation="agent"
						phoneNumber={vendor.phoneNumber ?? ''}
						websiteUrl={vendor.websiteUrl}
					/>
				))}
			</section>
		</section>
	);
}

/**
 * @param {{ settings: any, theme: any, state: any, address: { postalCode?: string }, categories: string[] }} props
 * @returns {JSX.Element}
 */
export function SelectedCategorySection(props) {
	const categoryState = props.state ?? {
		isLoading: false,
		localOfferList: [],
		offerList: [],
		tenantOfferList: [],
	};
	const sections = getCategorySections(categoryState, props.address);
	const appTitle = props.theme.appTitle ?? 'HomeKeepr';
	const agentName = props.theme.agentName?.trim()
		? props.theme.agentName.trim().split(/\s+/)[0]
		: translate('global.marketplace.sections.default_agent');
	const groupName =
		props.theme.agentGroup?.name ??
		translate('global.marketplace.sections.default_group');
	const categoryTitle = props.categories.map(getCategoryLabel).join(', ');

	return (
		<section className="mt-10">
			{categoryState.isLoading ? (
				<section className="py-8">
					<Loading />
				</section>
			) : null}
			{!categoryState.isLoading && !sections.hasAnyOffers ? (
				<NoContent
					message={translate(
						'global.local_pros_search.no_content.message',
						categoryTitle
					)}
					title={translate(
						'global.local_pros_search.no_content.title'
					)}
				/>
			) : null}
			{!categoryState.isLoading ? (
				<React.Fragment>
					<MarketplaceHeadlineSection
						title={`${appTitle} National Pros`}
						vendors={sections.tenantOffers}
					/>
					<MarketplaceHeadlineSection
						title={`${appTitle} Local Pros`}
						vendors={sections.localOffers}
					/>
					<OfferSection
						settings={props.settings}
						theme={props.theme}
						title={translate(
							'global.marketplace.sections.agent',
							agentName
						)}
						vendors={sections.recommendedOffers}
					/>
					<OfferSection
						settings={props.settings}
						theme={props.theme}
						title={translate(
							'global.marketplace.sections.group',
							groupName
						)}
						vendors={sections.groupOffers}
					/>
				</React.Fragment>
			) : null}
		</section>
	);
}

/**
 * @param {{ category: string, categories: string[], onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void }} props
 * @returns {JSX.Element | null}
 */
export function VendorCategoryFilter(props) {
	if (props.categories.length < 1) {
		return null;
	}

	return (
		<header className="marketplace-header mt-10">
			<h1 className="hl-ms-6 marketplace-header__title">{'Home Pros'}</h1>
			<section className="marketplace-category-filter">
				<fieldset className="input-group marketplace-category-filter__group">
					<label className="input-label" htmlFor="home-pros-category">
						{translate('agent.pages.offers.view.select_category')}
					</label>
					<div className="input-stylized">
						<select
							className="input"
							id="home-pros-category"
							name="category"
							onChange={props.onChange}
							value={props.category}
						>
							<option value="">
								{translate(
									'agent.pages.offers.view.all_categories'
								)}
							</option>
							{props.categories.map((category) => (
								<option key={category} value={category}>
									{getCategoryLabel(category)}
								</option>
							))}
						</select>
					</div>
				</fieldset>
			</section>
		</header>
	);
}
